import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart2,
  Users,
  Shield,
  Zap,
  Filter,
  RefreshCw,
  Download,
  AlertCircle,
  Layers,
  Search,
  X,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { sportsStatsService } from './sportsStatsService'
import {
  StatsFilters,
  StatsFilterOptions,
  OverviewStats,
  PlayerStatsRow,
  PlayerDetailStats,
  LineupStatsRow,
  OffenseBreakdown,
  DefenseBreakdown,
  ContributingSnap,
} from '../../types/sportsStats'
import { ContributingSnapsModal } from './ContributingSnapsModal'

interface StatsViewProps {
  teamId: string
}

export const StatsView: React.FC<StatsViewProps> = ({ teamId }) => {
  const { hasCapability } = useAuth()
  const canViewCollective = hasCapability('view_stats', teamId)

  // Subsección activa: 'overview' | 'players' | 'lineups' | 'offense' | 'defense'
  const [activeSection, setActiveSection] = useState<'overview' | 'players' | 'lineups' | 'offense' | 'defense'>(
    canViewCollective ? 'overview' : 'players'
  )

  // Filtros compartidos: simplificados a Concentración/Evento y Partido/Sesión (M8)
  const [filters, setFilters] = useState<StatsFilters>({})

  // Opciones de filtros
  const [filterOptions, setFilterOptions] = useState<StatsFilterOptions>({
    seasons: [],
    events: [],
    captures: [],
    athletes: [],
    concepts: [],
    calls: [],
  })

  // Eventos principales (concentraciones o eventos independientes)
  const topLevelEvents = React.useMemo(() => {
    return filterOptions.events.filter((e) => !e.parent_event_id || e.event_type === 'camp')
  }, [filterOptions.events])

  // IDs de eventos dependientes de la concentración seleccionada
  const childEventIds = React.useMemo(() => {
    if (!filters.eventId) return new Set<string>()
    return new Set(
      filterOptions.events
        .filter((e) => e.parent_event_id === filters.eventId)
        .map((e) => e.id)
    )
  }, [filterOptions.events, filters.eventId])

  // Capturas disponibles según concentración seleccionada
  const availableCaptures = React.useMemo(() => {
    if (!filters.eventId) return filterOptions.captures
    return filterOptions.captures.filter(
      (c) => c.event_id === filters.eventId || (c.event_id && childEventIds.has(c.event_id))
    )
  }, [filterOptions.captures, filters.eventId, childEventIds])

  // Cambio de concentración: limita opciones y limpia partido incompatible
  const handleEventChange = (newEventId: string | undefined) => {
    if (!newEventId) {
      setFilters((prev) => ({
        ...prev,
        eventId: undefined,
      }))
      return
    }

    const newChildIds = new Set(
      filterOptions.events
        .filter((e) => e.parent_event_id === newEventId)
        .map((e) => e.id)
    )
    const isCurrentCaptureValid =
      filters.captureId &&
      filterOptions.captures.some(
        (c) =>
          c.id === filters.captureId &&
          (c.event_id === newEventId || (c.event_id && newChildIds.has(c.event_id)))
      )

    setFilters((prev) => ({
      ...prev,
      eventId: newEventId,
      captureId: isCurrentCaptureValid ? prev.captureId : undefined,
    }))
  }

  // Cambio de partido/sesión
  const handleCaptureChange = (newCaptureId: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      captureId: newCaptureId || undefined,
    }))
  }

  // Estados de datos
  const [overview, setOverview] = useState<OverviewStats | null>(null)
  const [players, setPlayers] = useState<PlayerStatsRow[]>([])
  const [lineups, setLineups] = useState<LineupStatsRow[]>([])
  const [offenseBreakdown, setOffenseBreakdown] = useState<OffenseBreakdown | null>(null)
  const [defenseBreakdown, setDefenseBreakdown] = useState<DefenseBreakdown | null>(null)

  // Ficha detallada de jugador
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null)
  const [playerDetail, setPlayerDetail] = useState<PlayerDetailStats | null>(null)

  useModalLifecycle(Boolean(selectedAthleteId && playerDetail), () => setSelectedAthleteId(null))

  // Snaps contribuyentes (drill-down modal)
  const [contributingModalOpen, setContributingModalOpen] = useState(false)
  const [contributingTitle, setContributingTitle] = useState('')
  const [contributingSubtitle, setContributingSubtitle] = useState('')
  const [contributingSnaps, setContributingSnaps] = useState<ContributingSnap[]>([])
  const [loadingContributing, setLoadingContributing] = useState(false)
  const [lastMetricQuery, setLastMetricQuery] = useState<{
    key: string
    params: Record<string, unknown>
  } | null>(null)

  // Búsqueda en tabla de jugadores
  const [playerSearchQuery, setPlayerSearchQuery] = useState('')
  const [playerUnitFilter, setPlayerUnitFilter] = useState<'all' | 'offense' | 'defense'>('all')

  // Carga y errores
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // 1. Cargar opciones de filtros sin forzar filtros ocultos
  const loadFilterOptions = useCallback(async () => {
    try {
      const opts = await sportsStatsService.getFilters(teamId)
      setFilterOptions(opts)
    } catch (err: unknown) {
      console.error(err)
    }
  }, [teamId])

  useEffect(() => {
    loadFilterOptions()
  }, [loadFilterOptions])

  // 2. Cargar datos de la sección activa
  const loadSectionData = useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      if (activeSection === 'overview') {
        const data = await sportsStatsService.getOverview(teamId, filters)
        setOverview(data)
      } else if (activeSection === 'players') {
        const data = await sportsStatsService.getAllPlayers(teamId, filters)
        setPlayers(data)
      } else if (activeSection === 'lineups') {
        const data = await sportsStatsService.getLineups(teamId, filters)
        setLineups(data)
      } else if (activeSection === 'offense') {
        const data = await sportsStatsService.getOffenseBreakdown(teamId, filters)
        setOffenseBreakdown(data)
      } else if (activeSection === 'defense') {
        const data = await sportsStatsService.getDefenseBreakdown(teamId, filters)
        setDefenseBreakdown(data)
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar estadísticas.')
    } finally {
      setLoading(false)
    }
  }, [teamId, filters, activeSection])

  useEffect(() => {
    loadSectionData()
  }, [loadSectionData])

  // 3. Ficha individual de jugador
  const handleOpenPlayerDetail = async (athleteId: string) => {
    setSelectedAthleteId(athleteId)
    try {
      const detail = await sportsStatsService.getPlayerDetail(teamId, athleteId, filters)
      setPlayerDetail(detail)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar ficha de jugador.')
    }
  }

  // 4. Drilldown: Abrir snaps contribuyentes a una métrica
  const handleOpenContributing = async (
    metricKey: string,
    params: Record<string, unknown>,
    title: string,
    subtitle: string
  ) => {
    setContributingTitle(title)
    setContributingSubtitle(subtitle)
    setLastMetricQuery({ key: metricKey, params })
    setContributingModalOpen(true)
    setLoadingContributing(true)
    try {
      const snaps = await sportsStatsService.getContributingSnaps(teamId, metricKey, params, filters)
      setContributingSnaps(snaps)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar jugadas contribuyentes.')
    } finally {
      setLoadingContributing(false)
    }
  }

  const handleRecalculateContributing = async () => {
    if (!lastMetricQuery) return
    setLoadingContributing(true)
    try {
      const snaps = await sportsStatsService.getContributingSnaps(
        teamId,
        lastMetricQuery.key,
        lastMetricQuery.params,
        filters
      )
      setContributingSnaps(snaps)
      loadSectionData() // También refresca la vista general
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al recalcular datos.')
    } finally {
      setLoadingContributing(false)
    }
  }

  // 5. Limpieza de filtros (sin filtros ocultos)
  const handleResetFilters = () => {
    setFilters({})
  }

  // Construir resumen legible del ámbito activo para reportes y cabecera
  const getFilterSummary = (): string => {
    const parts: string[] = []
    if (filters.eventId) {
      const ev = filterOptions.events.find((item) => item.id === filters.eventId)
      if (ev) parts.push(`Concentración: ${ev.title}`)
    }
    if (filters.captureId) {
      const cap = filterOptions.captures.find((item) => item.id === filters.captureId)
      if (cap) parts.push(`Partido/Sesión: ${cap.title}`)
    }
    return parts.length > 0 ? parts.join(' • ') : 'Todos los eventos y partidos del equipo'
  }

  // 6. Exportaciones CSV
  const handleExportContributingSnapsCsv = () => {
    const headers = [
      'Secuencia',
      'Partido / Sesión',
      'Unidad',
      'Down',
      'Yardas Inicio',
      'Yardas Fin',
      'Yardas Netas',
      'Resultado',
      'Touchdown',
      'Lateral',
      'Puntos',
      'Lanzador',
      'Receptor',
      'Placador',
      'Concepto / Llamada',
      'Jugada',
      'Video URL',
      'Video Marca'
    ]

    const rows = contributingSnaps.map((s) => [
      s.sequence,
      s.match_title,
      s.our_side === 'offense' ? 'Ataque' : 'Defensa',
      s.down ?? 'N/D',
      s.yard_line_start ?? 'N/D',
      s.yard_line_end ?? 'N/D',
      s.net_yards !== null ? s.net_yards : 'N/D',
      s.primary_result ?? 'N/D',
      s.is_touchdown ? 'SÍ' : 'NO',
      s.had_lateral ? 'SÍ' : 'NO',
      s.points_awarded,
      s.passer_name ?? 'N/D',
      s.receiver_name ?? 'N/D',
      s.tackler_name ?? 'N/D',
      s.concept_or_call ?? 'N/D',
      s.play_name ?? 'N/D',
      s.video_url ?? 'N/D',
      s.video_timestamp_mark ?? 'N/D',
    ])

    sportsStatsService.exportToCsv('snaps_contribuyentes_m7', headers, rows, `${contributingTitle} (${getFilterSummary()})`)
  }

  const handleExportPlayersCsv = () => {
    const headers = [
      'Dorsal',
      'Nombre',
      'Posición',
      'Snaps Totales',
      'Snaps Ataque',
      'Snaps Defensa',
      'Pases Intentos',
      'Pases Completos',
      'Comp %',
      'Yardas Pase',
      'TDs Pase',
      'Intercepciones Lanzadas',
      'Targets',
      'Recepciones',
      'Rec %',
      'Drops',
      'Yardas Recepción',
      'TDs Recepción',
      'Carreras Intentos',
      'Yardas Carrera',
      'TDs Carrera',
      'Flags OK',
      'Flags Fallados',
      'Flag %',
      'Pases Defendidos',
      'Sacks',
      'Presiones',
      'Intercepciones Def',
      'TDs Defensivos'
    ]

    const rows = players.map((p) => [
      p.dorsal_number,
      `${p.first_name} ${p.last_name}`,
      p.primary_position,
      p.participation.total_snaps,
      p.participation.offense_snaps,
      p.participation.defense_snaps,
      p.passing.attempts,
      p.passing.completions,
      p.passing.comp_pct !== null ? `${p.passing.comp_pct}%` : 'N/D',
      p.passing.yards !== null ? p.passing.yards : 'N/D',
      p.passing.tds,
      p.passing.interceptions,
      p.receiving.targets,
      p.receiving.receptions,
      p.receiving.rec_pct !== null ? `${p.receiving.rec_pct}%` : 'N/D',
      p.receiving.drops,
      p.receiving.yards !== null ? p.receiving.yards : 'N/D',
      p.receiving.tds,
      p.rushing.attempts,
      p.rushing.yards !== null ? p.rushing.yards : 'N/D',
      p.rushing.tds,
      p.defending.flags_success,
      p.defending.flags_miss,
      p.defending.flag_pct !== null ? `${p.defending.flag_pct}%` : 'N/D',
      p.defending.passes_defended,
      p.defending.sacks,
      p.defending.pressures,
      p.defending.interceptions,
      p.defending.defensive_tds
    ])

    sportsStatsService.exportToCsv('estadisticas_jugadores_team_spain', headers, rows, getFilterSummary())
  }

  const handleExportLineupsCsv = () => {
    const headers = [
      'Unidad',
      'Integrantes (Dorsales)',
      'Jugadores',
      'Snaps Jugados',
      'Partidos',
      'Pases Comp',
      'Pases Att',
      'Comp %',
      'TDs Pase',
      'TDs Carrera',
      'Flags OK',
      'Flags Miss',
      'Sacks',
      'Intercepciones'
    ]

    const rows = lineups.map((l) => [
      l.unit === 'offense' ? 'Ataque' : 'Defensa',
      l.athlete_dorsals.map((d) => `#${d}`).join(' '),
      l.athlete_names.join(', '),
      l.snap_count,
      l.match_count,
      l.offense_metrics?.completions ?? 'N/D',
      l.offense_metrics?.attempts ?? 'N/D',
      l.offense_metrics?.comp_pct !== null && l.offense_metrics?.comp_pct !== undefined ? `${l.offense_metrics.comp_pct}%` : 'N/D',
      l.offense_metrics?.pass_tds ?? 'N/D',
      l.offense_metrics?.rush_tds ?? 'N/D',
      l.defense_metrics?.flags_ok ?? 'N/D',
      l.defense_metrics?.flags_miss ?? 'N/D',
      l.defense_metrics?.sacks ?? 'N/D',
      l.defense_metrics?.interceptions ?? 'N/D'
    ])

    sportsStatsService.exportToCsv('quintetos_reales_team_spain', headers, rows, getFilterSummary())
  }

  // Filtrado de jugadores en UI
  const filteredPlayers = players.filter((p) => {
    if (playerUnitFilter === 'offense' && p.participation.offense_snaps === 0) return false
    if (playerUnitFilter === 'defense' && p.participation.defense_snaps === 0) return false
    if (playerSearchQuery.trim()) {
      const q = playerSearchQuery.toLowerCase()
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase()
      const dorsal = String(p.dorsal_number)
      return fullName.includes(q) || dorsal.includes(q) || p.primary_position.toLowerCase().includes(q)
    }
    return true
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* 1. Barra de Filtros Compartidos */}
      <div
        className="card"
        style={{
          padding: '1.25rem',
          backgroundColor: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: 'var(--radius-md, 8px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={16} style={{ color: 'var(--brand-red, #d9232c)' }} />
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
              Filtros de Ámbito Deportivo
            </h3>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={handleResetFilters}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary, #64748b)',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                textDecoration: 'underline',
              }}
            >
              <span>Limpiar filtros</span>
            </button>
            <button
              onClick={loadSectionData}
              className="btn btn-secondary"
              style={{ padding: '0.35rem 0.65rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              title="Recargar estadísticas con los filtros actuales"
            >
              <RefreshCw size={13} className={loading ? 'spin' : ''} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>

        {/* Controles de filtro simplificados (M8) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '0.85rem',
          }}
        >
          {/* Concentración / Evento */}
          <div>
            <label
              htmlFor="stats-filter-event"
              style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}
            >
              Concentración / Evento
            </label>
            <select
              id="stats-filter-event"
              value={filters.eventId || ''}
              onChange={(e) => handleEventChange(e.target.value || undefined)}
              style={{ width: '100%', padding: '0.5rem', fontSize: '0.85rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
            >
              <option value="">Todas las concentraciones/eventos</option>
              {topLevelEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </div>

          {/* Partido / Sesión */}
          <div>
            <label
              htmlFor="stats-filter-capture"
              style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}
            >
              Partido / Sesión
            </label>
            <select
              id="stats-filter-capture"
              value={filters.captureId || ''}
              onChange={(e) => handleCaptureChange(e.target.value || undefined)}
              style={{ width: '100%', padding: '0.5rem', fontSize: '0.85rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
            >
              <option value="">Todos los partidos/sesiones</option>
              {availableCaptures.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title} ({c.snap_count} snaps)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Resumen del ámbito activo */}
        <div
          style={{
            marginTop: '0.85rem',
            paddingTop: '0.65rem',
            borderTop: '1px dashed #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.8rem',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <div>
            <span style={{ color: 'var(--text-secondary, #64748b)' }}>Ámbito activo: </span>
            <strong style={{ color: 'var(--brand-blue, #1e3a8a)' }}>{getFilterSummary()}</strong>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="badge badge-muted" style={{ fontSize: '0.72rem' }}>
              Exclusiones automáticas: no-play, anulados y borradores
            </span>
          </div>
        </div>
      </div>

      {/* 2. Selector de Subsecciones (Segmented Control Responsivo BK-09) */}
      <div className="stats-subnav-tabs">
        {canViewCollective && (
          <button
            type="button"
            onClick={() => setActiveSection('overview')}
            className={`stats-subnav-btn ${activeSection === 'overview' ? 'active' : ''}`}
          >
            <BarChart2 size={16} />
            <span>Resumen Global</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setActiveSection('players')}
          className={`stats-subnav-btn ${activeSection === 'players' ? 'active' : ''}`}
        >
          <Users size={16} />
          <span>Jugadores {canViewCollective ? `(${players.length})` : '(Mi Ficha)'}</span>
        </button>

        {canViewCollective && (
          <button
            type="button"
            onClick={() => setActiveSection('lineups')}
            className={`stats-subnav-btn ${activeSection === 'lineups' ? 'active' : ''}`}
          >
            <Layers size={16} />
            <span>Quintetos Reales ({lineups.length})</span>
          </button>
        )}

        {canViewCollective && (
          <button
            type="button"
            onClick={() => setActiveSection('offense')}
            className={`stats-subnav-btn ${activeSection === 'offense' ? 'active' : ''}`}
          >
            <Zap size={16} />
            <span>Ataque Colectivo</span>
          </button>
        )}

        {canViewCollective && (
          <button
            type="button"
            onClick={() => setActiveSection('defense')}
            className={`stats-subnav-btn ${activeSection === 'defense' ? 'active' : ''}`}
          >
            <Shield size={16} />
            <span>Defensa Colectiva</span>
          </button>
        )}
      </div>

      {/* 3. CONTENIDO DE LA SUBSECCIÓN */}

      {/* ESTADO DE CARGA */}
      {loading && (
        <div
          className="card"
          style={{
            padding: '3rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            color: 'var(--text-secondary)',
          }}
        >
          <RefreshCw size={28} className="animate-spin" style={{ color: 'var(--brand-blue, #1e3a8a)' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Cargando estadísticas deportivas...</span>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Consultando datos consolidados y filtros</span>
        </div>
      )}

      {/* ESTADO DE ERROR CON REINTENTAR */}
      {errorMsg && !loading && (
        <div
          className="card"
          style={{
            padding: '2rem',
            backgroundColor: 'rgba(217, 35, 44, 0.04)',
            border: '1px solid rgba(217, 35, 44, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            textAlign: 'center',
          }}
        >
          <AlertCircle size={32} style={{ color: 'var(--brand-red, #d9232c)' }} />
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)' }}>
            No se pudieron cargar las estadísticas
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', maxWidth: '480px' }}>
            {errorMsg}
          </div>
          <button
            type="button"
            onClick={() => {
              loadFilterOptions()
              loadSectionData()
            }}
            className="btn btn-primary"
            style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}
          >
            <RefreshCw size={15} />
            <span>Reintentar</span>
          </button>
        </div>
      )}

      {/* ESTADO VACÍO EN RESUMEN GLOBAL */}
      {!loading && !errorMsg && activeSection === 'overview' && (!overview || (overview.snaps?.total ?? 0) === 0) && (
        <div
          className="card"
          style={{
            padding: '3rem 2rem',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <BarChart2 size={36} style={{ color: 'var(--text-muted)' }} />
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)' }}>
            No hay jugadas ni estadísticas en este ámbito
          </div>
          <div style={{ fontSize: '0.85rem', maxWidth: '450px' }}>
            {getFilterSummary()}
          </div>
          <button
            type="button"
            onClick={handleResetFilters}
            className="btn btn-secondary"
            style={{ fontSize: '0.82rem', marginTop: '0.5rem' }}
          >
            Restablecer todos los filtros
          </button>
        </div>
      )}

      {/* SECCIÓN A: RESUMEN GLOBAL */}
      {!loading && !errorMsg && activeSection === 'overview' && overview && (overview.snaps?.total ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Tarjetas de volumen de snaps y muestra */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
            }}
          >
            <div
              className="card"
              style={{
                padding: '1.25rem',
                borderTop: '4px solid var(--brand-blue, #1e3a8a)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.45rem',
              }}
            >
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Snaps Válidos (Jugadas Únicas)
              </div>
              <div
                style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--brand-blue, #1e3a8a)', cursor: 'pointer', lineHeight: 1.1 }}
                onClick={() => handleOpenContributing('overview_snaps', {}, 'Todos los snaps válidos (jugadas únicas)', getFilterSummary())}
                title="Pulsar para consultar todos los 294 snaps únicos del ámbito"
              >
                {overview.snaps?.total ?? 0}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                <span>Participaciones:</span>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--brand-blue, #1e3a8a)', fontWeight: 600 }}
                  onClick={() => handleOpenContributing('overview_offense_snaps', {}, 'Snaps con participación de Ataque', getFilterSummary())}
                  title="Ver snaps con participación de ataque"
                >
                  {overview.snaps?.offense ?? 0} Atq
                </span>
                <span>•</span>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--brand-blue, #1e3a8a)', fontWeight: 600 }}
                  onClick={() => handleOpenContributing('overview_defense_snaps', {}, 'Snaps con participación de Defensa', getFilterSummary())}
                  title="Ver snaps con participación de defensa"
                >
                  {overview.snaps?.defense ?? 0} Def
                </span>
              </div>
              <div
                style={{
                  fontSize: '0.72rem',
                  color: '#64748b',
                  backgroundColor: '#f1f5f9',
                  padding: '0.25rem 0.45rem',
                  borderRadius: '4px',
                  lineHeight: 1.35,
                }}
              >
                <strong>{overview.snaps?.offense_only ?? 0}</strong> solo ataque · <strong>{overview.snaps?.defense_only ?? 0}</strong> solo defensa ·{' '}
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 700, color: 'var(--brand-blue, #1e3a8a)' }}
                  onClick={() => handleOpenContributing('overview_both_snaps', {}, 'Snaps inter-escuadra (ambas unidades en campo)', getFilterSummary())}
                  title="Ver 31 snaps inter-escuadra de Team practice"
                >
                  {overview.snaps?.both_units ?? 0} inter-escuadra (ambas unidades)
                </span>
              </div>
            </div>

            <div
              className="card"
              style={{
                padding: '1.25rem',
                borderTop: '4px solid #10b981',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
              }}
            >
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Touchdowns Anotados
              </div>
              <div
                style={{ fontSize: '2rem', fontWeight: 800, color: '#059669', cursor: 'pointer' }}
                onClick={() => handleOpenContributing('overview_tds', {}, 'Todos los Touchdowns anotados', getFilterSummary())}
                title="Pulsar para consultar los snaps de TD"
              >
                {overview.offense?.total_tds ?? 0}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Pase: <strong>{overview.offense?.pass_tds ?? 0}</strong> • Carrera: <strong>{overview.offense?.rush_tds ?? 0}</strong>
              </div>
            </div>

            <div
              className="card"
              style={{
                padding: '1.25rem',
                borderTop: '4px solid var(--brand-red, #d9232c)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
              }}
            >
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Eficacia de Placaje (Flags)
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--brand-red, #d9232c)' }}>
                {overview.defense?.flag_pct !== null && overview.defense?.flag_pct !== undefined ? `${overview.defense.flag_pct}%` : 'N/D'}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Conseguidos: <strong>{overview.defense?.flags_success ?? 0}</strong> • Fallados: <strong>{overview.defense?.flags_miss ?? 0}</strong>
              </div>
            </div>

            <div
              className="card"
              style={{
                padding: '1.25rem',
                borderTop: '4px solid #f59e0b',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
              }}
            >
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Jugadas Excluidas (Integridad)
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#d97706', marginTop: '0.25rem' }}>
                {(overview.exclusions?.noplay_snaps ?? 0) + (overview.exclusions?.void_snaps ?? 0) + (overview.exclusions?.draft_snaps ?? 0)}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                No-play: {overview.exclusions?.noplay_snaps ?? 0} • Anuladas: {overview.exclusions?.void_snaps ?? 0} • Borradores: {overview.exclusions?.draft_snaps ?? 0}
              </div>
            </div>
          </div>

          {/* Comparativa Ataque vs Defensa en 2 columnas */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {/* Tarjeta Detalle Ataque */}
            <div className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Zap size={18} style={{ color: 'var(--brand-blue, #1e3a8a)' }} />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Rendimiento de Ataque</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.88rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Pases completos / Intentos:</span>
                  <span
                    style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--brand-blue, #1e3a8a)' }}
                    onClick={() => handleOpenContributing('offense_passes', {}, 'Pases de Ataque', getFilterSummary())}
                  >
                    {overview.offense?.completions ?? 0} / {overview.offense?.attempts ?? 0} ({overview.offense?.comp_pct !== null && overview.offense?.comp_pct !== undefined ? `${overview.offense.comp_pct}%` : 'N/D'})
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Carreras registradas:</span>
                  <span
                    style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--brand-blue, #1e3a8a)' }}
                    onClick={() => handleOpenContributing('offense_runs', {}, 'Carreras de Ataque', getFilterSummary())}
                  >
                    {overview.offense?.rush_attempts ?? 0} jugadas
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Yardas netas longitudinales conocidas:</span>
                  <span style={{ fontWeight: 600 }}>
                    {overview.offense?.known_net_yards ?? 0} yds{' '}
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      (cobertura: {overview.offense?.net_yards_known_snaps ?? 0}/{overview.snaps?.offense ?? 0} snaps)
                    </span>
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Conversiones Totales:</span>
                  <span style={{ fontWeight: 600 }}>
                    {overview.offense?.conversion_success ?? 0} / {overview.offense?.conversion_attempts ?? 0}{' '}
                    ({overview.offense?.conv_pct !== null && overview.offense?.conv_pct !== undefined ? `${overview.offense.conv_pct}%` : 'N/D'})
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                  <span style={{ color: '#64748b' }}>Desglose Conversiones:</span>
                  <span style={{ fontWeight: 600 }}>
                    1 pt: {overview.offense?.conv_1pt_success ?? 0}/{overview.offense?.conv_1pt_attempts ?? 0} · 2 pts: {overview.offense?.conv_2pt_success ?? 0}/{overview.offense?.conv_2pt_attempts ?? 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Tarjeta Detalle Defensa */}
            <div className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Shield size={18} style={{ color: 'var(--brand-red, #d9232c)' }} />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Rendimiento de Defensa</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.88rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Placajes (Flags conseguidos / Intentos):</span>
                  <span style={{ fontWeight: 600 }}>
                    {overview.defense?.flags_success ?? 0} / {overview.defense?.flag_attempts ?? 0} ({overview.defense?.flag_pct !== null && overview.defense?.flag_pct !== undefined ? `${overview.defense.flag_pct}%` : 'N/D'})
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Sacks y Presiones del Blitzer:</span>
                  <span
                    style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--brand-blue, #1e3a8a)' }}
                    onClick={() => handleOpenContributing('defense_sacks', {}, 'Sacks defensivos', getFilterSummary())}
                  >
                    {overview.defense?.sacks ?? 0} sacks • {overview.defense?.pressures ?? 0} presiones
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Pases defendidos e Intercepciones:</span>
                  <span
                    style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--brand-blue, #1e3a8a)' }}
                    onClick={() => handleOpenContributing('defense_turnovers', {}, 'Turnovers defensivos', getFilterSummary())}
                  >
                    {overview.defense?.passes_defended ?? 0} PD • <strong>{overview.defense?.interceptions ?? 0} INT</strong>
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.4rem', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>Touchdowns concedidos al rival:</span>
                  <span style={{ fontWeight: 600, color: (overview.defense?.tds_conceded ?? 0) > 0 ? '#b91c1c' : '#059669' }}>
                    {overview.defense?.tds_conceded ?? 0} TDs concedidos
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN B: JUGADORES (TABLA COMPLETA CON DRILLDOWN) */}
      {activeSection === 'players' && (
        <div className="card" style={{ padding: '1.25rem', overflow: 'hidden' }}>
          {/* Cabecera y buscador */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
              marginBottom: '1rem',
            }}
          >
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.2rem 0' }}>
                Estadísticas Individuales de la Plantilla
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Pase con lanzador real (incluyendo Hydra), targets conocidos vs N/D, yardas laterales y defensa.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {/* Buscador */}
              <div style={{ position: 'relative', width: '200px' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Buscar jugador..."
                  value={playerSearchQuery}
                  onChange={(e) => setPlayerSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '0.35rem 0.5rem 0.35rem 1.75rem', fontSize: '0.82rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              {/* Filtro Unidad */}
              <select
                value={playerUnitFilter}
                onChange={(e) => setPlayerUnitFilter(e.target.value as 'all' | 'offense' | 'defense')}
                style={{ padding: '0.35rem', fontSize: '0.82rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              >
                <option value="all">Todas las posiciones</option>
                <option value="offense">Ataque</option>
                <option value="defense">Defensa</option>
              </select>

              <button
                onClick={handleExportPlayersCsv}
                className="btn btn-secondary"
                style={{ padding: '0.35rem 0.65rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                title="Exportar a CSV compatible con Excel"
              >
                <Download size={14} />
                <span>Exportar CSV</span>
              </button>
            </div>
          </div>

          {/* Tabla de jugadores (Escritorio) */}
          <div className="stats-players-desktop-table table-responsive">
            <table className="table" style={{ width: '100%', fontSize: '0.83rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                  <th style={{ padding: '0.6rem 0.75rem' }}>Dorsal</th>
                  <th style={{ padding: '0.6rem 0.75rem' }}>Jugador</th>
                  <th style={{ padding: '0.6rem 0.75rem' }}>Posición</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Snaps (Atq/Def)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Pases (C/Att)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Yds / TD Pase</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Rec (C/Tgt)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Yds / TD Rec</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Carreras (Att/TD)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Flags (OK/Miss)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>PD / Sack / INT</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                      No hay jugadores con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filteredPlayers.map((p) => (
                    <tr
                      key={p.athlete_id}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      onClick={() => handleOpenPlayerDetail(p.athlete_id)}
                      className="table-row-hover"
                    >
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: 'var(--brand-blue, #1e3a8a)' }}>
                        #{p.dorsal_number}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>
                        {p.first_name} {p.last_name}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <span className="badge badge-muted">{p.primary_position}</span>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        <strong>{p.participation.total_snaps}</strong>{' '}
                        <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                          ({p.participation.offense_snaps}/{p.participation.defense_snaps})
                        </span>
                      </td>

                      {/* Pase */}
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.passing.attempts > 0 ? (
                          <span
                            style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline' }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenContributing(
                                'player_passes',
                                { athlete_id: p.athlete_id },
                                `Pases de #${p.dorsal_number} ${p.first_name}`,
                                getFilterSummary()
                              )
                            }}
                          >
                            {p.passing.completions}/{p.passing.attempts}{' '}
                            <span style={{ fontSize: '0.74rem' }}>
                              ({p.passing.comp_pct !== null ? `${p.passing.comp_pct}%` : 'N/D'})
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.passing.attempts > 0 ? (
                          <span>
                            {p.passing.yards !== null ? `${p.passing.yards} yds` : 'N/D'} • <strong>{p.passing.tds} TD</strong>
                            {p.passing.interceptions > 0 && (
                              <span style={{ color: '#b91c1c', fontSize: '0.74rem' }}> ({p.passing.interceptions} INT)</span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>

                      {/* Recepción */}
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.receiving.targets > 0 ? (
                          <span
                            style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline' }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenContributing(
                                'player_receptions',
                                { athlete_id: p.athlete_id },
                                `Recepciones de #${p.dorsal_number} ${p.first_name}`,
                                getFilterSummary()
                              )
                            }}
                          >
                            {p.receiving.receptions}/{p.receiving.targets}{' '}
                            <span style={{ fontSize: '0.74rem' }}>
                              ({p.receiving.rec_pct !== null ? `${p.receiving.rec_pct}%` : 'N/D'})
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.receiving.receptions > 0 ? (
                          <span>
                            {p.receiving.yards !== null ? `${p.receiving.yards} yds` : 'N/D'} • <strong>{p.receiving.tds} TD</strong>
                            {p.receiving.drops > 0 && (
                              <span style={{ color: '#d97706', fontSize: '0.74rem' }}> ({p.receiving.drops} drops)</span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>

                      {/* Carrera */}
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.rushing.attempts > 0 ? (
                          <span>
                            {p.rushing.attempts} att • {p.rushing.yards !== null ? `${p.rushing.yards} yds` : 'N/D'}
                            {p.rushing.tds > 0 && <strong> ({p.rushing.tds} TD)</strong>}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>

                      {/* Defensa: Flags */}
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {(p.defending.flags_success > 0 || p.defending.flags_miss > 0) ? (
                          <span
                            style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline' }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenContributing(
                                'player_flags',
                                { athlete_id: p.athlete_id },
                                `Placajes de #${p.dorsal_number} ${p.first_name}`,
                                getFilterSummary()
                              )
                            }}
                          >
                            <strong>{p.defending.flags_success}</strong> / {p.defending.flags_miss}{' '}
                            <span style={{ fontSize: '0.74rem' }}>
                              ({p.defending.flag_pct !== null ? `${p.defending.flag_pct}%` : 'N/D'})
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>

                      {/* Defensa: Big Plays */}
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {(p.defending.passes_defended > 0 || p.defending.sacks > 0 || p.defending.interceptions > 0) ? (
                          <span>
                            {p.defending.passes_defended} PD • {p.defending.sacks} SK •{' '}
                            <strong>{p.defending.interceptions} INT</strong>
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>0</span>
                        )}
                      </td>

                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenPlayerDetail(p.athlete_id)
                          }}
                          className="btn btn-secondary"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          Ver Ficha
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Tarjetas móviles para jugadores */}
          <div className="stats-players-mobile-cards">
            {filteredPlayers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                No hay jugadores con los filtros seleccionados.
              </div>
            ) : (
              filteredPlayers.map((p) => (
                <div key={p.athlete_id} className="player-card-mobile">
                  <div className="player-card-header">
                    <div className="player-card-name-group">
                      <span className="lineup-dorsal-pill">#{p.dorsal_number}</span>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>
                        {p.first_name} {p.last_name}
                      </strong>
                    </div>
                    <span className="badge badge-muted" style={{ fontSize: '0.72rem' }}>
                      {p.primary_position}
                    </span>
                  </div>

                  {/* Participación */}
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    Participación: <strong>{p.participation.total_snaps} snaps</strong>{' '}
                    <span>({p.participation.offense_snaps} Atq / {p.participation.defense_snaps} Def)</span>
                  </div>

                  {/* Resumen de métricas clave */}
                  <div className="player-card-metrics-grid">
                    {/* Pase */}
                    {p.passing.attempts > 0 && (
                      <div className="player-metric-pill">
                        <span className="player-metric-pill-label">Pases (C/Att)</span>
                        <span
                          className="player-metric-pill-val"
                          style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() =>
                            handleOpenContributing(
                              'player_passes',
                              { athlete_id: p.athlete_id },
                              `Pases de #${p.dorsal_number} ${p.first_name}`,
                              getFilterSummary()
                            )
                          }
                        >
                          {p.passing.completions}/{p.passing.attempts} ({p.passing.yards ?? 0} yd, {p.passing.tds} TD)
                        </span>
                      </div>
                    )}

                    {/* Recepción */}
                    {p.receiving.targets > 0 && (
                      <div className="player-metric-pill">
                        <span className="player-metric-pill-label">Recepción (C/Tgt)</span>
                        <span
                          className="player-metric-pill-val"
                          style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() =>
                            handleOpenContributing(
                              'player_receptions',
                              { athlete_id: p.athlete_id },
                              `Recepciones de #${p.dorsal_number} ${p.first_name}`,
                              getFilterSummary()
                            )
                          }
                        >
                          {p.receiving.receptions}/{p.receiving.targets} ({p.receiving.yards ?? 0} yd, {p.receiving.tds} TD)
                        </span>
                      </div>
                    )}

                    {/* Carrera */}
                    {p.rushing.attempts > 0 && (
                      <div className="player-metric-pill">
                        <span className="player-metric-pill-label">Carreras</span>
                        <span className="player-metric-pill-val">
                          {p.rushing.attempts} att ({p.rushing.yards ?? 0} yd, {p.rushing.tds} TD)
                        </span>
                      </div>
                    )}

                    {/* Placajes / Flags */}
                    {(p.defending.flags_success > 0 || p.defending.flags_miss > 0) && (
                      <div className="player-metric-pill">
                        <span className="player-metric-pill-label">Flags (OK/Miss)</span>
                        <span
                          className="player-metric-pill-val"
                          style={{ color: 'var(--brand-blue, #1e3a8a)', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() =>
                            handleOpenContributing(
                              'player_flags',
                              { athlete_id: p.athlete_id },
                              `Placajes de #${p.dorsal_number} ${p.first_name}`,
                              getFilterSummary()
                            )
                          }
                        >
                          {p.defending.flags_success} / {p.defending.flags_miss}
                        </span>
                      </div>
                    )}

                    {/* Big Plays Defensa */}
                    {(p.defending.passes_defended > 0 || p.defending.sacks > 0 || p.defending.interceptions > 0) && (
                      <div className="player-metric-pill">
                        <span className="player-metric-pill-label">Big Plays Def</span>
                        <span className="player-metric-pill-val" style={{ color: 'var(--brand-red, #d9232c)' }}>
                          {p.defending.interceptions} INT • {p.defending.sacks} SK • {p.defending.passes_defended} PD
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleOpenPlayerDetail(p.athlete_id)}
                    className="btn btn-secondary"
                    style={{ width: '100%', padding: '0.45rem', fontSize: '0.8rem', justifyContent: 'center' }}
                  >
                    Ver Ficha Completa
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* SECCIÓN C: QUINTETOS REALES */}
      {activeSection === 'lineups' && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.2rem 0' }}>
                Rendimiento de Quintetos Reales en Campo
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Agrupación estricta por los 5 jugadores reales que participaron en cada snap (cualquier sustitución genera un grupo distinto).
              </p>
            </div>

            <button
              onClick={handleExportLineupsCsv}
              className="btn btn-secondary"
              style={{ padding: '0.35rem 0.65rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <Download size={14} />
              <span>Exportar Quintetos</span>
            </button>
          </div>

          {/* Tabla de quintetos (Escritorio) */}
          <div className="stats-lineup-desktop-table table-responsive">
            <table className="table" style={{ width: '100%', fontSize: '0.83rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                  <th style={{ padding: '0.6rem 0.75rem' }}>Unidad</th>
                  <th style={{ padding: '0.6rem 0.75rem' }}>Integrantes (5 Jugadores Reales)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Snaps (Muestra)</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Partidos</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Métricas Ofensivas</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Métricas Defensivas</th>
                  <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lineups.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                      No se registraron quintetos para el ámbito actual.
                    </td>
                  </tr>
                ) : (
                  lineups.map((l, lIdx) => (
                    <tr key={lIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <span className={`badge ${l.unit === 'offense' ? 'badge-blue' : 'badge-red'}`}>
                          {l.unit === 'offense' ? 'Ataque' : 'Defensa'}
                        </span>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {l.athlete_names.map((name, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                              <span className="lineup-dorsal-pill">#{l.athlete_dorsals[idx] ?? '—'}</span>
                              <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{name}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        <strong>{l.snap_count}</strong> snaps
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {l.match_count} partidos
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {l.unit === 'offense' && l.offense_metrics ? (
                          <span>
                            {l.offense_metrics.completions}/{l.offense_metrics.attempts}{' '}
                            ({l.offense_metrics.comp_pct !== null ? `${l.offense_metrics.comp_pct}%` : 'N/D'}) •{' '}
                            <strong>{l.offense_metrics.pass_tds + l.offense_metrics.rush_tds} TD</strong>
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {l.unit === 'defense' && l.defense_metrics ? (
                          <span>
                            Flags: {l.defense_metrics.flags_ok}/{l.defense_metrics.flags_miss} •{' '}
                            <strong>{l.defense_metrics.interceptions} INT</strong> • {l.defense_metrics.sacks} SK
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() =>
                            handleOpenContributing(
                              'lineup_snaps',
                              { lineup_hash: l.lineup_hash, unit: l.unit },
                              `Snaps del quinteto (${l.unit === 'offense' ? 'Ataque' : 'Defensa'})`,
                              l.athlete_names.join(', ')
                            )
                          }
                          className="btn btn-secondary"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          Ver Snaps ({l.snap_count})
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Tarjetas móviles para quintetos */}
          <div className="stats-lineup-mobile-cards">
            {lineups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                No se registraron quintetos para el ámbito actual.
              </div>
            ) : (
              lineups.map((l, lIdx) => (
                <div key={lIdx} className="lineup-card-mobile">
                  <div className="lineup-card-header">
                    <span className={`badge ${l.unit === 'offense' ? 'badge-blue' : 'badge-red'}`}>
                      {l.unit === 'offense' ? 'Ataque' : 'Defensa'}
                    </span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <strong>{l.snap_count}</strong> snaps • {l.match_count} partidos
                    </span>
                  </div>

                  <div className="lineup-card-players">
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.15rem' }}>
                      5 Jugadores en Campo
                    </div>
                    {l.athlete_names.map((name, idx) => (
                      <div key={idx} className="lineup-card-player-item">
                        <span className="lineup-dorsal-pill">#{l.athlete_dorsals[idx] ?? '—'}</span>
                        <span style={{ fontWeight: 500 }}>{name}</span>
                      </div>
                    ))}
                  </div>

                  <div className="lineup-card-metrics">
                    {l.unit === 'offense' && l.offense_metrics ? (
                      <>
                        <div className="lineup-metric-block">
                          <span className="lineup-metric-label">Pases (C/Att)</span>
                          <span className="lineup-metric-value">
                            {l.offense_metrics.completions}/{l.offense_metrics.attempts}{' '}
                            <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                              ({l.offense_metrics.comp_pct !== null ? `${l.offense_metrics.comp_pct}%` : 'N/D'})
                            </span>
                          </span>
                        </div>
                        <div className="lineup-metric-block">
                          <span className="lineup-metric-label">Touchdowns</span>
                          <span className="lineup-metric-value" style={{ color: 'var(--brand-blue, #1e3a8a)' }}>
                            {l.offense_metrics.pass_tds + l.offense_metrics.rush_tds} TD
                          </span>
                        </div>
                      </>
                    ) : l.unit === 'defense' && l.defense_metrics ? (
                      <>
                        <div className="lineup-metric-block">
                          <span className="lineup-metric-label">Placajes Flags</span>
                          <span className="lineup-metric-value">
                            {l.defense_metrics.flags_ok} / {l.defense_metrics.flags_miss}
                          </span>
                        </div>
                        <div className="lineup-metric-block">
                          <span className="lineup-metric-label">Big Plays</span>
                          <span className="lineup-metric-value" style={{ color: 'var(--brand-red, #d9232c)' }}>
                            {l.defense_metrics.interceptions} INT • {l.defense_metrics.sacks} SK
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="lineup-metric-block" style={{ gridColumn: 'span 2' }}>
                        <span className="lineup-metric-label">Métricas</span>
                        <span className="lineup-metric-value" style={{ color: '#94a3b8' }}>Sin eventos destacados</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() =>
                      handleOpenContributing(
                        'lineup_snaps',
                        { lineup_hash: l.lineup_hash, unit: l.unit },
                        `Snaps del quinteto (${l.unit === 'offense' ? 'Ataque' : 'Defensa'})`,
                        l.athlete_names.join(', ')
                      )
                    }
                    className="btn btn-secondary"
                    style={{ width: '100%', padding: '0.45rem', fontSize: '0.8rem', justifyContent: 'center' }}
                  >
                    Ver Snaps Contribuyentes ({l.snap_count})
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* SECCIÓN D: DESGLOSE DE ATAQUE COLECTIVO */}
      {activeSection === 'offense' && offenseBreakdown && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Desglose por concepto */}
          <div className="card" style={{ padding: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              Rendimiento por Concepto Ofensivo
            </h3>
            <div className="table-responsive">
              <table className="table" style={{ width: '100%', fontSize: '0.83rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                    <th style={{ padding: '0.6rem 0.75rem' }}>Concepto</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Snaps</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Pases (C/Att)</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Comp %</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Yardas Netas</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Touchdowns</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Turnovers</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {offenseBreakdown.by_concept.map((c, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{c.concept_name}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.snaps}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.completions}/{c.attempts}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {c.comp_pct !== null ? `${c.comp_pct}%` : 'N/D'}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.yards} yds</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{c.tds}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: c.turnovers > 0 ? '#b91c1c' : '#64748b' }}>
                        {c.turnovers}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() =>
                            handleOpenContributing(
                              'concept_snaps',
                              { concept_name: c.concept_name },
                              `Snaps del concepto: ${c.concept_name}`,
                              getFilterSummary()
                            )
                          }
                          className="btn btn-secondary"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          Ver Snaps
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Desglose por jugada */}
          <div className="card" style={{ padding: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              Rendimiento por Jugada Ofensiva
            </h3>
            <div className="table-responsive">
              <table className="table" style={{ width: '100%', fontSize: '0.83rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                    <th style={{ padding: '0.6rem 0.75rem' }}>Jugada</th>
                    <th style={{ padding: '0.6rem 0.75rem' }}>Concepto</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Snaps</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Pases (C/Att)</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Comp %</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Yardas Netas</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Touchdowns</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {offenseBreakdown.by_play.map((p, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{p.play_name}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#64748b' }}>{p.concept_name}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{p.snaps}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{p.completions}/{p.attempts}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {p.comp_pct !== null ? `${p.comp_pct}%` : 'N/D'}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{p.yards} yds</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{p.tds}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() =>
                            handleOpenContributing(
                              'play_snaps',
                              { play_name: p.play_name },
                              `Snaps de la jugada: ${p.play_name}`,
                              getFilterSummary()
                            )
                          }
                          className="btn btn-secondary"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          Ver Snaps
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN E: DESGLOSE DE DEFENSA COLECTIVA */}
      {activeSection === 'defense' && defenseBreakdown && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Desglose por llamada defensiva */}
          <div className="card" style={{ padding: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              Rendimiento por Llamada Defensiva
            </h3>
            <div className="table-responsive">
              <table className="table" style={{ width: '100%', fontSize: '0.83rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                    <th style={{ padding: '0.6rem 0.75rem' }}>Llamada Defensiva</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Snaps Defensivos</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Flags (OK/Miss)</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Eficacia Flag %</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Sacks</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>Intercepciones</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>TD Concedidos</th>
                    <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {defenseBreakdown.by_call.map((c, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{c.call_name}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.snaps}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.flags_ok}/{c.flags_miss}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                        {c.flag_pct !== null ? `${c.flag_pct}%` : 'N/D'}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{c.sacks}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--brand-blue, #1e3a8a)' }}>
                        {c.interceptions}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: c.tds_conceded > 0 ? '#b91c1c' : '#059669' }}>
                        {c.tds_conceded}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() =>
                            handleOpenContributing(
                              'call_snaps',
                              { call_name: c.call_name },
                              `Snaps de la llamada: ${c.call_name}`,
                              getFilterSummary()
                            )
                          }
                          className="btn btn-secondary"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          Ver Snaps
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FICHA INDIVIDUAL DE JUGADOR */}
      {selectedAthleteId && playerDetail && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 15, 30, 0.65)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 990,
            padding: '1rem',
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '800px',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              borderRadius: 'var(--radius-lg, 12px)',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span
                  style={{
                    backgroundColor: 'var(--brand-blue, #1e3a8a)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    padding: '0.3rem 0.75rem',
                    borderRadius: '6px',
                  }}
                >
                  #{playerDetail.overview.dorsal_number}
                </span>
                <div>
                  <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
                    {playerDetail.overview.first_name} {playerDetail.overview.last_name}
                  </h2>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Posición nominal: <strong>{playerDetail.overview.primary_position}</strong> • {getFilterSummary()}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedAthleteId(null)
                  setPlayerDetail(null)
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem', WebkitOverflowScrolling: 'touch' }}>
              {/* Tarjetas resumen del jugador */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                <div style={{ backgroundColor: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Snaps Jugados</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--brand-blue, #1e3a8a)' }}>
                    {playerDetail.overview.participation.total_snaps}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Atq: {playerDetail.overview.participation.offense_snaps} • Def: {playerDetail.overview.participation.defense_snaps}
                  </div>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Touchdowns Propios</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#059669' }}>
                    {playerDetail.overview.receiving.tds + playerDetail.overview.rushing.tds + playerDetail.overview.defending.defensive_tds}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Rec: {playerDetail.overview.receiving.tds} • Rush: {playerDetail.overview.rushing.tds} • Def: {playerDetail.overview.defending.defensive_tds}
                  </div>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Pase (Lanzador Real)</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: '0.2rem' }}>
                    {playerDetail.overview.passing.completions}/{playerDetail.overview.passing.attempts} ({playerDetail.overview.passing.comp_pct !== null ? `${playerDetail.overview.passing.comp_pct}%` : 'N/D'})
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {playerDetail.overview.passing.yards !== null ? `${playerDetail.overview.passing.yards} yds` : 'Yds N/D'} • {playerDetail.overview.passing.tds} TD
                  </div>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Placajes (Flags)</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: '0.2rem' }}>
                    {playerDetail.overview.defending.flags_success} OK / {playerDetail.overview.defending.flags_miss} Miss
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Eficacia: {playerDetail.overview.defending.flag_pct !== null ? `${playerDetail.overview.defending.flag_pct}%` : 'N/D'}
                  </div>
                </div>
              </div>

              {/* Evolución partido a partido */}
              <div>
                <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                  Evolución por Partido / Sesión
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table" style={{ width: '100%', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem' }}>Partido</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Snaps</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Pase (C/Att)</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Rec (C/Tgt)</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Flags (OK/Miss)</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>SK / INT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playerDetail.by_match.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>
                            Sin partidos registrados para este jugador.
                          </td>
                        </tr>
                      ) : (
                        playerDetail.by_match.map((m, mIdx) => (
                          <tr key={mIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.5rem', fontWeight: 600 }}>{m.match_title}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{m.snaps}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {m.passing_att > 0 ? `${m.passing_cmp}/${m.passing_att}` : '0'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {m.targets > 0 ? `${m.receptions}/${m.targets}` : '0'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {(m.flags_ok > 0 || m.flags_miss > 0) ? `${m.flags_ok}/${m.flags_miss}` : '0'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {m.sacks} SK • {m.interceptions} INT
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ padding: '0.85rem 1.5rem', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setSelectedAthleteId(null)
                  setPlayerDetail(null)
                }}
                className="btn btn-secondary"
              >
                Cerrar Ficha
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DRILL-DOWN: SNAPS CONTRIBUYENTES */}
      <ContributingSnapsModal
        isOpen={contributingModalOpen}
        onClose={() => setContributingModalOpen(false)}
        title={contributingTitle}
        subtitle={contributingSubtitle}
        snaps={contributingSnaps}
        loading={loadingContributing}
        onRecalculate={handleRecalculateContributing}
        onExportCsv={handleExportContributingSnapsCsv}
      />
    </div>
  )
}
